/**
 * Secret Santa Event View Page
 * Shows event details, participants, and manages the event
 */

import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { UserMenu } from '@/components/auth/UserMenu'
import { LoginButton } from '@/components/auth/LoginButton'
import { Button } from '@/components/common/Button'
import { Modal } from '@/components/common/Modal'
import { ConfirmDialog } from '@/components/common/ConfirmDialog'
import { ParticipantForm } from '@/components/secretSanta/ParticipantForm'
import { useSecretSanta } from '@/hooks/useSecretSanta'
import {
  getSecretSantaByToken,
  addParticipant,
  removeParticipant,
  drawNames,
  ParticipantFormData,
} from '@/services/secretSanta.service'
import toast from 'react-hot-toast'

export const SecretSantaView = () => {
  const { accessToken } = useParams<{ accessToken: string }>()
  const { user } = useAuth()
  const [santaId, setSantaId] = useState<string | undefined>(undefined)
  const { event, loading } = useSecretSanta(santaId)
  const [isJoinModalOpen, setIsJoinModalOpen] = useState(false)
  const [loadingAction, setLoadingAction] = useState(false)
  const [participantToRemove, setParticipantToRemove] = useState<string | null>(null)
  const [showDrawNamesDialog, setShowDrawNamesDialog] = useState(false)

  useEffect(() => {
    if (!accessToken) return

    const fetchEvent = async () => {
      try {
        const eventData = await getSecretSantaByToken(accessToken)
        setSantaId(eventData.id)
      } catch (err) {
        console.error('Error loading event:', err)
        toast.error('Failed to load Secret Santa event')
      }
    }

    fetchEvent()
  }, [accessToken])

  const handleJoin = async (data: ParticipantFormData) => {
    if (!event) return

    try {
      setLoadingAction(true)
      await addParticipant(event.id, data)
      toast.success('Joined Secret Santa event!')
      setIsJoinModalOpen(false)
    } catch (error: any) {
      toast.error(error.message || 'Failed to join event')
      throw error
    } finally {
      setLoadingAction(false)
    }
  }

  const handleRemoveParticipant = async () => {
    if (!event || !participantToRemove) return

    try {
      setLoadingAction(true)
      await removeParticipant(event.id, participantToRemove)
      toast.success('Participant removed')
      setParticipantToRemove(null)
    } catch (error: any) {
      toast.error(error.message || 'Failed to remove participant')
    } finally {
      setLoadingAction(false)
    }
  }

  const handleDrawNames = async () => {
    if (!event) return

    try {
      setLoadingAction(true)
      const result = await drawNames(event.id)
      toast.success(`${result.message} - ${result.assignmentsCount} assignments created`)
      setShowDrawNamesDialog(false)
    } catch (error: any) {
      toast.error(error.message || 'Failed to draw names')
      setShowDrawNamesDialog(false)
    } finally {
      setLoadingAction(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading event...</p>
        </div>
      </div>
    )
  }

  if (!event) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md">
          <div className="w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-12 h-12 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Event Not Found</h2>
          <p className="text-gray-600 mb-6">
            This Secret Santa event may have been deleted or the link is invalid.
          </p>
          <Link to="/" className="text-blue-600 hover:text-blue-700 font-medium">
            Go to Homepage
          </Link>
        </div>
      </div>
    )
  }

  const isCreator = user?.uid === event.creatorId
  const isParticipant = event.participants.some((p) => p.userId === user?.uid)

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Paiseh!
            </Link>
            {user ? <UserMenu /> : <LoginButton />}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Event Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-gray-900">{event.title}</h1>
              {event.description && (
                <p className="text-gray-600 mt-2">{event.description}</p>
              )}
              <div className="flex items-center gap-4 mt-4 text-sm text-gray-500">
                {event.eventDate && (
                  <span>
                    📅 {new Date(event.eventDate.seconds * 1000).toLocaleDateString()}
                  </span>
                )}
                {event.budget && (
                  <span>
                    💰 Budget: {event.currency} ${(event.budget / 100).toFixed(2)}
                  </span>
                )}
                <span>👥 {event.participants.length} participants</span>
                {event.isDrawn && <span className="text-green-600 font-medium">✓ Names Drawn</span>}
              </div>
            </div>

            {isCreator && !event.isDrawn && (
              <Button
                onClick={() => setShowDrawNamesDialog(true)}
                disabled={loadingAction || event.participants.length < 2}
              >
                Draw Names
              </Button>
            )}
          </div>

          {/* Share Link */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Share this link to invite participants:
            </label>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={event.shareLink}
                readOnly
                className="flex-1 px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm font-mono"
              />
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  navigator.clipboard.writeText(event.shareLink)
                  toast.success('Link copied to clipboard')
                }}
              >
                Copy
              </Button>
            </div>
          </div>
        </div>

        {/* Join Prompt */}
        {user && !isParticipant && !event.isDrawn && (
          <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-6">
            <div className="flex items-start gap-4">
              <svg className="w-6 h-6 text-blue-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
              </svg>
              <div className="flex-1">
                <h3 className="font-semibold text-blue-900 mb-2">Join this event</h3>
                <p className="text-blue-700 mb-4">
                  Add yourself as a participant to take part in the gift exchange.
                </p>
                <Button onClick={() => setIsJoinModalOpen(true)}>
                  Join Secret Santa
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* My Assignment */}
        {event.isDrawn && isParticipant && (
          <div className="mb-6">
            <Link to={`/secret-santa/${accessToken}/assignment`}>
              <div className="bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg p-6 text-white hover:shadow-lg transition-shadow">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-semibold mb-1">View Your Assignment</h3>
                    <p className="text-purple-100">See who you're giving a gift to</p>
                  </div>
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </div>
              </div>
            </Link>
          </div>
        )}

        {/* Participants List */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Participants ({event.participants.length})
          </h2>

          {event.participants.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No participants yet. Be the first to join!
            </div>
          ) : (
            <div className="space-y-3">
              {event.participants.map((participant, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-semibold">
                      {participant.name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{participant.name}</p>
                      <p className="text-sm text-gray-500">{participant.email}</p>
                    </div>
                    {participant.wishlistId && (
                      <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full">
                        Wishlist linked
                      </span>
                    )}
                  </div>

                  {(isCreator || participant.userId === user?.uid) && !event.isDrawn && (
                    <button
                      onClick={() => setParticipantToRemove(participant.userId!)}
                      disabled={loadingAction}
                      className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Join Modal */}
      <Modal
        isOpen={isJoinModalOpen}
        onClose={() => setIsJoinModalOpen(false)}
        title="Join Secret Santa"
      >
        <ParticipantForm
          onSubmit={handleJoin}
          onCancel={() => setIsJoinModalOpen(false)}
        />
      </Modal>

      {/* Remove Participant Dialog */}
      <ConfirmDialog
        isOpen={!!participantToRemove}
        onClose={() => setParticipantToRemove(null)}
        onConfirm={handleRemoveParticipant}
        title="Remove Participant"
        message="Are you sure you want to remove this participant from the Secret Santa event?"
        variant="warning"
        confirmText="Remove"
        cancelText="Cancel"
        isLoading={loadingAction}
      />

      {/* Draw Names Dialog */}
      <ConfirmDialog
        isOpen={showDrawNamesDialog}
        onClose={() => setShowDrawNamesDialog(false)}
        onConfirm={handleDrawNames}
        title="Draw Names"
        message={`Ready to draw names for ${event?.participants.length || 0} participants? Once drawn, participants cannot be added or removed.`}
        variant="info"
        confirmText="Draw Names"
        cancelText="Cancel"
        isLoading={loadingAction}
      />
    </div>
  )
}
